import type {
  WorkbookCatalogEntry,
  WorkbookColumnarSheet,
  WorkbookColumnType,
  WorkbookDefinition,
  WorkbookEntityFilter,
  WorkbookSource,
} from '@erp/shared-types';
import { parseFormula } from '@erp/workbooks';
import type { SchemaGroup } from './workbookSchemaGroup';

export type WorkbookCatalogKind = WorkbookCatalogEntry['kind'];

/** The catalog's groups, in the order the Add Data panel lists them. */
export const WORKBOOK_CATALOG_KINDS = [
  'data-source',
  'mapping',
  'stateful-table',
  'data-asset',
  'entity',
  'forecast-snapshots',
] as const satisfies readonly WorkbookCatalogKind[];

/** Past this many entries, the Add Data panel offers a search. */
export const CATALOG_SEARCH_THRESHOLD = 8;

/** Whether `entry`'s name, label or description contains `needle` (lower case); an empty needle matches. */
export function catalogEntryMatches(
  entry: Pick<WorkbookCatalogEntry, 'name' | 'label' | 'description'>,
  needle: string,
): boolean {
  return [entry.name, entry.label, entry.description].some((text) =>
    text?.toLowerCase().includes(needle),
  );
}

/**
 * The members of a schema group that the search shows: all of them when the
 * schema's name matches, else those matching as the catalog entry matches (a
 * member the capped catalog lacks matches on its name alone).
 */
export function matchingGroupMembers<T extends { id: string; name: string }>(
  group: Pick<SchemaGroup, 'schemaName'>,
  members: readonly T[],
  catalog: readonly WorkbookCatalogEntry[],
  query: string,
): T[] {
  const needle = query.trim().toLowerCase();
  if (!needle || group.schemaName.toLowerCase().includes(needle))
    return [...members];
  return members.filter((member) =>
    catalogEntryMatches(
      catalog.find((entry) => entry.id === member.id) ?? member,
      needle,
    ),
  );
}

/**
 * The catalog grouped by kind, each group only when the user may Read that kind and
 * it has an entry whose name contains `query`, ignoring case.
 */
export function catalogGroups(
  entries: readonly WorkbookCatalogEntry[],
  query: string,
  canRead: (kind: WorkbookCatalogKind) => boolean,
): { kind: WorkbookCatalogKind; entries: WorkbookCatalogEntry[] }[] {
  const needle = query.trim().toLowerCase();
  return WORKBOOK_CATALOG_KINDS.flatMap((kind) => {
    if (!canRead(kind)) return [];
    const matching = entries.filter(
      (entry) => entry.kind === kind && catalogEntryMatches(entry, needle),
    );
    return matching.length ? [{ kind, entries: matching }] : [];
  });
}

/**
 * What the Add Data panel lists: the catalog's kinds as `catalogGroups` gives
 * them, except that Data Sources sharing a schema (`schemaGroups`, which the
 * server lists uncapped) sit under their schema rather than on their own.
 * `schemaGroups` shown are those whose name, or a data source's, matches `query`;
 * `memberships` (schema id to its complete membership, once loaded) lets a data
 * source the capped catalog lacks make its group match.
 */
export function catalogView(
  entries: readonly WorkbookCatalogEntry[],
  sources: readonly WorkbookSource[],
  schemaGroups: readonly SchemaGroup[],
  query: string,
  canRead: (kind: WorkbookCatalogKind) => boolean,
  memberships: ReadonlyMap<
    string,
    readonly { id: string; name: string }[]
  > = new Map(),
) {
  const needle = query.trim().toLowerCase();
  const shownGroups = schemaGroups.filter(
    (group) =>
      !needle ||
      group.schemaName.toLowerCase().includes(needle) ||
      entries.some(
        (entry) =>
          entry.schemaId === group.schemaId &&
          catalogEntryMatches(entry, needle),
      ) ||
      matchingGroupMembers(
        group,
        memberships.get(group.schemaId) ?? [],
        entries,
        query,
      ).length > 0,
  );
  const grouped = new Set(schemaGroups.map((group) => group.schemaId));
  const available = unaddedEntries(entries, sources);
  const plain = available.filter(
    (entry) =>
      entry.kind !== 'data-source' ||
      !entry.schemaId ||
      !grouped.has(entry.schemaId),
  );
  const sections = catalogGroups(plain, query, canRead);
  if (shownGroups.length && !sections.some((s) => s.kind === 'data-source'))
    sections.unshift({ kind: 'data-source', entries: [] });
  return { available, sections, shownGroups };
}

/** The kinds whose absence the Add Data panel explains; Entities are always offered to who can Read them. */
const GAP_KINDS = ['data-source', 'mapping', 'data-asset'] as const;

/**
 * Why a kind is missing from the Add Data panel: the user can Read it but the
 * organization has none yet (`empty`), or the user can't Read it (`unreadable`).
 */
export function catalogGaps(
  entries: readonly WorkbookCatalogEntry[],
  canRead: (kind: WorkbookCatalogKind) => boolean,
): { empty: WorkbookCatalogKind[]; unreadable: WorkbookCatalogKind[] } {
  return {
    empty: GAP_KINDS.filter(
      (kind) => canRead(kind) && !entries.some((entry) => entry.kind === kind),
    ),
    unreadable: GAP_KINDS.filter((kind) => !canRead(kind)),
  };
}

/** The Source a catalog entry adds: a Data Asset follows its Latest version. */
export function catalogSource(entry: WorkbookCatalogEntry): WorkbookSource {
  const item = {
    id: crypto.randomUUID(),
    name: entry.label || entry.name,
    folder: '',
  };
  switch (entry.kind) {
    case 'mapping':
      return { ...item, kind: 'mapping', mappingId: entry.id };
    case 'stateful-table':
      return { ...item, kind: 'stateful-table', tableId: entry.id };
    case 'data-source':
      return { ...item, kind: 'data-source', linkId: entry.id };
    case 'data-asset':
      return { ...item, kind: 'data-asset', dataAssetId: entry.id };
    case 'forecast-snapshots':
      return { ...item, kind: 'forecast-snapshots' };
    case 'entity':
      return { ...item, kind: 'entity', entity: entry.id };
  }
}

/** Unused entries, plus forecasts that can be added again with different run filters. */
export function unaddedEntries(
  entries: readonly WorkbookCatalogEntry[],
  sources: readonly WorkbookSource[],
): WorkbookCatalogEntry[] {
  const added = new Set(sources.map(catalogEntryId));
  return entries.filter(
    (entry) => entry.kind === 'forecast-snapshots' || !added.has(entry.id),
  );
}

/** The catalog entry a Source reads, for the kinds the catalog lists. */
export function catalogEntryId(source: WorkbookSource): string | undefined {
  switch (source.kind) {
    case 'mapping':
      return source.mappingId;
    case 'stateful-table':
      return source.tableId;
    case 'data-source':
      return source.linkId;
    case 'data-asset':
      return source.dataAssetId;
    case 'forecast-snapshots':
      return 'forecast-snapshots';
    case 'entity':
      return source.entity;
    default:
      return undefined;
  }
}

/** Which version a Data Asset Source reads, as its origin describes it. */
export type DataAssetVersionState =
  /** The asset's Latest version; its number once the catalog has loaded. */
  | { kind: 'latest'; versionNumber?: number }
  /** A Pinned version; unavailable when the catalog doesn't list it as uploaded. */
  | { kind: 'pinned'; versionNumber?: number; available: boolean };

export function dataAssetVersionState(
  source: Extract<WorkbookSource, { kind: 'data-asset' }>,
  entry: WorkbookCatalogEntry | undefined,
): DataAssetVersionState {
  const numberOf = (id: string | null | undefined) =>
    entry?.versions?.find((version) => version.id === id)?.versionNumber;
  if (!source.versionId)
    return { kind: 'latest', versionNumber: numberOf(entry?.latestVersionId) };
  const versionNumber = numberOf(source.versionId);
  return {
    kind: 'pinned',
    versionNumber,
    available: !entry || versionNumber !== undefined,
  };
}

/** Pins a Data Asset Source to a version, or with none, returns it to Latest. */
export function pinDataAssetVersion(
  definition: WorkbookDefinition,
  sourceId: string,
  versionId: string | undefined,
): WorkbookDefinition {
  return {
    ...definition,
    sources: definition.sources.map((source) => {
      if (source.id !== sourceId || source.kind !== 'data-asset') return source;
      const { versionId: _pinned, ...latest } = source;
      return versionId ? { ...latest, versionId } : latest;
    }),
  };
}

/**
 * The Declared type each of a Source's columns naturally has, by column name: an
 * Entity's columns are typed by the entity. Other Sources' columns are text.
 */
export function sourceColumnTypes(
  source: WorkbookSource,
  catalog: readonly WorkbookCatalogEntry[] | undefined,
): ReadonlyMap<string, WorkbookColumnType> {
  if (source.kind !== 'entity' && source.kind !== 'forecast-snapshots')
    return new Map();
  const entry = catalog?.find(
    (candidate) =>
      candidate.kind === source.kind && candidate.id === catalogEntryId(source),
  );
  return new Map(
    (entry?.columns ?? []).map((column) => [column.name, column.type]),
  );
}

/** Replaces an Entity Source's filters; with none, the Source reads every row. */
export function setEntityFilters(
  definition: WorkbookDefinition,
  sourceId: string,
  filters: readonly WorkbookEntityFilter[],
): WorkbookDefinition {
  return {
    ...definition,
    sources: definition.sources.map((source) => {
      if (source.id !== sourceId || source.kind !== 'entity') return source;
      const { filters: _previous, ...unfiltered } = source;
      return filters.length
        ? { ...unfiltered, filters: [...filters] }
        : unfiltered;
    }),
  };
}

/**
 * The type a Source gives a column that only reads one of its columns, when the
 * column declares another: an opt-in update for Worksheets created before the
 * Source typed that column (a timestamp read as Text before Date and time
 * existed). Null for a Formula, a column already of that type, or a Source
 * column that is Text.
 */
export function sourceTypeUpdate(
  column: Pick<WorkbookColumnarSheet['columns'][number], 'expression' | 'type'>,
  sourceTypes: ReadonlyMap<string, WorkbookColumnType> | undefined,
): WorkbookColumnType | null {
  const parsed = parseFormula(column.expression);
  if (!parsed.ok || parsed.formula.kind !== 'column') return null;
  const type = sourceTypes?.get(parsed.formula.name);
  return type && type !== 'text' && type !== column.type ? type : null;
}

/** The types a Worksheet's input Source gives its columns; none when the input is a Worksheet. */
export function sheetSourceTypes(
  definition: Pick<WorkbookDefinition, 'sources'>,
  sheet: Pick<WorkbookColumnarSheet, 'inputId'>,
  catalog: readonly WorkbookCatalogEntry[] | undefined,
): ReadonlyMap<string, WorkbookColumnType> | undefined {
  const source = definition.sources.find((item) => item.id === sheet.inputId);
  return source ? sourceColumnTypes(source, catalog) : undefined;
}
